/**
 * Swiper initialization for all site sliders.
 * Run after document ready (uses DOM selectors).
 */
import Swiper from 'swiper';
import { Autoplay, EffectFade, FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules';
import 'swiper/swiper-bundle.css';

const renderFraction = (currentClass: string, totalClass: string) =>
    `<div class="swiper-pagination-wrapper"><span class="${currentClass}"></span> / <span class="${totalClass}"></span></div>`;

export const initSliders = () => {
    // ──────────────────────────────────────────────
    // SINGLE-ITEM / TEXT SLIDERS
    // ──────────────────────────────────────────────

    // Text slider – fade, autoplay, 1 slide. Speed via data-speed (ms).
    document.querySelectorAll<HTMLElement>('.text-slider').forEach((el) => {
        if (el.querySelector('.swiper-wrapper')) return;
        wrapChildrenAsSwiper(el, ':scope > div');
        const speed = Number.parseInt(el.dataset.speed ?? '', 10) || 1000;
        new Swiper(el, {
            speed,
            loop: false,
            effect: 'fade',
            fadeEffect: { crossFade: true },
            autoplay: { delay: 4000, disableOnInteraction: false },
            modules: [EffectFade, Autoplay],
        });
    });

    // ──────────────────────────────────────────────
    // VERHALEN SLIDER – synced dual swipers
    // ──────────────────────────────────────────────
    initVerhalenSlider();

    // ──────────────────────────────────────────────
    // PRODUCT SLIDER (with thumbs)
    // ──────────────────────────────────────────────
    initProductSlider();

    // ──────────────────────────────────────────────
    // IMAGE CAROUSELS
    // ──────────────────────────────────────────────
    initImageCarousels();
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// VERHALEN SLIDER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function initVerhalenSlider() {
    const firstEl = document.querySelector('.verhalenSection .first-swiper') as HTMLElement | null;
    const secondEl = document.querySelector(
        '.verhalenSection .second-swiper'
    ) as HTMLElement | null;
    if (!firstEl || !secondEl) return;

    const firstSwiper = new Swiper(firstEl, {
        autoHeight: true,
        loop: true,
        breakpoints: {
            1024: { slidesPerView: 2 },
        },
        modules: [Navigation],
        speed: 1000,
        navigation: {
            nextEl: '.verhalenSection .arrow.next',
            prevEl: '.verhalenSection .arrow.prev',
        },
        spaceBetween: 0,
    });

    const secondSwiper = new Swiper(secondEl, {
        autoHeight: true,
        loop: true,
        slidesPerView: 1,
        modules: [EffectFade],
        effect: 'fade',
    });

    firstSwiper.on('slideChangeTransitionEnd', () => {
        const index = firstSwiper.realIndex;
        secondSwiper.slideTo(index, 1000, false);

        const paging = document.querySelector('.verhalenSection .pagingInfo');
        if (paging) {
            const page = String(index + 1).padStart(2, '0');
            const total = String(firstSwiper.slides.length).padStart(2, '0');
            paging.innerHTML = `${page} / ${total}`;
        }
    });
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PRODUCT SLIDER with thumbnails
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function initProductSlider() {
    const mainEl = document.querySelector('.product-detail .slider-swiper') as HTMLElement | null;
    const thumbsEl = document.querySelector('.product-detail .slider-thumbs') as HTMLElement | null;
    if (!mainEl || !thumbsEl) return;

    const thumbsSwiper = new Swiper(thumbsEl, {
        spaceBetween: 10,
        slidesPerView: 4,
        freeMode: true,
        watchSlidesProgress: true,
        modules: [FreeMode],
    });

    new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 30,
        loop: true,
        autoplay: { delay: 5000 },
        pagination: {
            el: mainEl.querySelector('.swiper-pagination') as HTMLElement,
            type: 'fraction',
            renderFraction,
        },
        navigation: {
            nextEl: mainEl.querySelector('.arrow.next') as HTMLElement,
            prevEl: mainEl.querySelector('.arrow.prev') as HTMLElement,
        },
        thumbs: { swiper: thumbsSwiper },
        modules: [Navigation, Pagination, Autoplay, Thumbs],
    });
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// IMAGE CAROUSELS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function initImageCarousels() {
    // Image showcase – shared component (home, footer, afbeeldingslider).
    // Loop and autoplay are read from the track's data attributes so each
    // template can opt in or out via the imageShowcase Twig partial.
    document.querySelectorAll('.imageShowcase-track').forEach((el) => {
        const container = el as HTMLElement;
        if (container.classList.contains('swiper-initialized')) return;

        const loop = container.dataset.loop !== 'false';
        const autoplay = container.dataset.autoplay !== 'false';
        const section = container.closest('.imageShowcase');

        // Authored slide count, captured before any cloning. Used for the
        // progress bar so it tracks the original carousel length, not the
        // padded DOM count after pre-init clones.
        const originalSlides = Array.from(
            container.querySelectorAll<HTMLElement>(':scope > .imageShowcase-slide')
        );
        const realSlideCount = originalSlides.length;

        // With slidesPerView + loop, Swiper silently disables loop when
        // total slide width < 2× viewport. Clone slides if there are too
        // few; only relevant when loop is on.
        if (loop && realSlideCount > 0 && realSlideCount < 8) {
            const cloneRounds = Math.ceil(8 / realSlideCount) - 1;
            for (let round = 0; round < cloneRounds; round++) {
                originalSlides.forEach((slide) => {
                    const clone = slide.cloneNode(true) as HTMLElement;
                    clone.setAttribute('aria-hidden', 'true');
                    container.appendChild(clone);
                });
            }
        }

        initCarousel(container, {
            nextEl: section?.querySelector('.arrowWrappers .arrow.next') as HTMLElement | null,
            prevEl: section?.querySelector('.arrowWrappers .arrow.prev') as HTMLElement | null,
            progressBar: section?.querySelector(
                '.imageShowcase-progressBar'
            ) as HTMLElement | null,
            slideSelector: '.imageShowcase-slide',
            loop,
            autoplay,
            realSlideCount,
            slidesPerView: 1,
            centeredSlides: true,
            spaceBetween: 16,
            breakpoints: {
                768: { slidesPerView: 1.2, spaceBetween: 6 },
                1024: { slidesPerView: 1.9, spaceBetween: 6 },
                1280: { slidesPerView: 2.1, spaceBetween: 8 },
                1600: { slidesPerView: 2.4, spaceBetween: 8 },
            },
            loopAdditionalSlides: loop ? 2 : 0,
        });
    });

    // Simple image carousel – `.slider-swiper` containers rendered by the
    // image-carousel component (afbeeling-tekst block, cursussen home
    // block). Single slide visible, arrows + fraction pagination from the
    // template. Product-detail uses its own main+thumbs handler.
    document.querySelectorAll('.slider-swiper').forEach((el) => {
        const container = el as HTMLElement;
        if (container.closest('.product-detail')) return;
        if (container.classList.contains('swiper-initialized')) return;

        const slideCount = container.querySelectorAll('.swiper-slide').length;
        if (slideCount === 0) return;
        const multiSlide = slideCount > 1;

        // Arrows + fraction pagination markup come from the component and
        // are only rendered when there are multiple slides.
        const nextEl = container.querySelector('.arrow.next') as HTMLElement | null;
        const prevEl = container.querySelector('.arrow.prev') as HTMLElement | null;
        const paginationEl = container.querySelector('.swiper-pagination') as HTMLElement | null;

        // data-peek="true" enables the tablet peek (auto-width centered
        // ≥600px, back to slidesPerView 1 ≥1024px). Default is plain
        // slidesPerView 1 across all viewports.
        const peek = container.dataset.peek === 'true';
        const loop = container.dataset.loop === 'true';
        const autoplay = container.dataset.autoplay === 'true';
        const breakpoints = peek
            ? {
                  600: { slidesPerView: 'auto' as const, centeredSlides: true },
                  1024: { slidesPerView: 1, centeredSlides: false },
              }
            : undefined;

        const modules: typeof Navigation[] = [];
        if (multiSlide && nextEl && prevEl) modules.push(Navigation);
        if (multiSlide && paginationEl) modules.push(Pagination);
        if (autoplay) modules.push(Autoplay);

        new Swiper(container, {
            speed: 500,
            loop,
            autoplay: autoplay ? { delay: 3000, disableOnInteraction: false } : false,
            slidesPerView: 1,
            spaceBetween: peek ? 20 : 0,
            breakpoints,
            navigation:
                multiSlide && nextEl && prevEl
                    ? { nextEl: nextEl as HTMLElement, prevEl: prevEl as HTMLElement }
                    : undefined,
            pagination:
                multiSlide && paginationEl
                    ? { el: paginationEl, type: 'fraction', renderFraction }
                    : undefined,
            modules,
        });
    });

}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SHARED CAROUSEL FACTORY
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface CarouselOptions {
    nextEl?: HTMLElement | null;
    prevEl?: HTMLElement | null;
    progressBar?: HTMLElement | null;
    slideSelector: string;
    loop?: boolean;
    autoplay?: boolean;
    slidesPerView?: number | 'auto';
    centeredSlides?: boolean;
    spaceBetween?: number;
    breakpoints?: Record<number, { slidesPerView?: number | 'auto'; centeredSlides?: boolean; spaceBetween?: number }>;
    loopAdditionalSlides?: number;
    /** Authored slide count, used for progress-bar math in loop mode where
     * the DOM slide count includes our own pre-init clones. */
    realSlideCount?: number;
}

function initCarousel(container: HTMLElement, opts: CarouselOptions): Swiper {
    // Wrap existing children in swiper-wrapper if the markup hasn't done so.
    if (!container.querySelector('.swiper-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';
        container.querySelectorAll(`:scope > ${opts.slideSelector}`).forEach((el) => {
            (el as HTMLElement).classList.add('swiper-slide');
            wrapper.appendChild(el);
        });
        container.classList.add('swiper');
        container.innerHTML = '';
        container.appendChild(wrapper);
    }

    const navConfig: { nextEl?: HTMLElement; prevEl?: HTMLElement } = {};
    if (opts.nextEl) navConfig.nextEl = opts.nextEl;
    if (opts.prevEl) navConfig.prevEl = opts.prevEl;

    const autoplayEnabled = opts.autoplay !== false;
    const loopEnabled = opts.loop ?? true;
    const modules = [Navigation];
    if (autoplayEnabled) modules.push(Autoplay);

    // In loop mode Swiper's `progress` event is non-linear because the
    // duplicated loop-slides are counted in the journey. Use realIndex
    // against the authored slide count instead, resetting per loop.
    const updateProgressBar = (s: Swiper) => {
        if (!opts.progressBar) return;
        if (loopEnabled && opts.realSlideCount && opts.realSlideCount > 1) {
            const idx = s.realIndex % opts.realSlideCount;
            const ratio = idx / (opts.realSlideCount - 1);
            opts.progressBar.style.width = `${Math.max(0, Math.min(1, ratio)) * 100}%`;
        } else if (!loopEnabled) {
            const clamped = Math.max(0, Math.min(1, s.progress));
            opts.progressBar.style.width = `${clamped * 100}%`;
        }
    };

    return new Swiper(container, {
        speed: 500,
        loop: loopEnabled,
        autoplay: autoplayEnabled ? { delay: 3000, disableOnInteraction: false } : false,
        slidesPerView: opts.slidesPerView ?? 'auto',
        centeredSlides: opts.centeredSlides ?? false,
        spaceBetween: opts.spaceBetween ?? 10,
        breakpoints: opts.breakpoints,
        loopAdditionalSlides: opts.loopAdditionalSlides,
        navigation: Object.keys(navConfig).length ? navConfig : undefined,
        modules,
        on: {
            init: updateProgressBar,
            progress(s) {
                if (!loopEnabled) updateProgressBar(s);
            },
            slideChange(s) {
                if (loopEnabled) updateProgressBar(s);
            },
        },
    });
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// HELPER: wrap direct children in swiper-wrapper
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function wrapChildrenAsSwiper(container: HTMLElement, selector?: string) {
    if (container.querySelector('.swiper-wrapper')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';

    const children = selector
        ? container.querySelectorAll(selector)
        : container.querySelectorAll(':scope > *');

    children.forEach((el) => {
        (el as HTMLElement).classList.add('swiper-slide');
        wrapper.appendChild(el);
    });

    container.classList.add('swiper');
    container.innerHTML = '';
    container.appendChild(wrapper);
}
